import type { FormValidationResult } from "./form-validation.type";
import type { ApiError, ApiErrorDataErrors } from "@/services/api";

import set from "lodash/set";

import { isValidObject, isEmptyObject } from "@/utilities/object";

export function validationErrorParser(
  error?: ApiError,
  supportedErrorNames: string[] = [],
  otherErrorName: string = "others"
): ApiErrorDataErrors {
  if (!error) return {};

  const errors = getErrorDataFromApiResponse(error);

  let others: string[] = [];
  if (errors && typeof errors === "object" && !Array.isArray(errors)) {
    const errorObj = errors as Record<string, string>;
    others = Object.keys(errorObj)
      .filter((key) => !supportedErrorNames.includes(key))
      .map((key) => Array(errorObj[key]).join("\n"));
  } else if (errors && typeof errors == "string") {
    others = [errors];
  }

  const formatted: ApiErrorDataErrors = {};
  if (errors && typeof errors === "object" && !Array.isArray(errors)) {
    const errorObj = errors as Record<string, string>;
    for (const errorName of supportedErrorNames) {
      formatted[errorName] = errorObj[errorName]
        ? Array(errorObj[errorName]).join("\n")
        : "";
    }
  } else {
    for (const errorName of supportedErrorNames) {
      formatted[errorName] = "";
    }
  }
  formatted[otherErrorName] = others.length
    ? others.map((o) => String(o)).join("\n")
    : "";
  return formatted;
}

export const getErrorDataFromApiResponse = (error?: ApiError) => {
  return (
    error?.response?.data?.errors ??
    error?.response?.data?.message ??
    error?.message ??
    error
  );
};

export const getFieldErrorProps = (error = ""): FormValidationResult => {
  if (!error) return {};

  return {
    validateStatus: "error",
    help: error,
  };
};

export const getDynamicFieldsErrors = (
  error: ApiError,
  dynamicFieldsNames: Record<string, string[]> = {},
  othersKey: string = "others"
) => {
  const errors: Record<string, Record<string, string>[]> = {};
  const others: string[] = [];

  const errorData = getErrorDataFromApiResponse(error);

  if (!errorData || !isValidObject(errorData) || isEmptyObject(errorData)) {
    return errors;
  }

  for (const errorKey of Object.keys(errorData)) {
    const [dynamicKey, _index, dynamicField] = errorKey.split(".");

    const dynamicFieldExist =
      dynamicFieldsNames?.[dynamicKey]?.includes(dynamicField);

    // TODO IMP-1415 fix TS issue
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const errorDataValue = errorData[errorKey] ?? [];

    if (dynamicFieldExist) {
      const value = errorDataValue.join(", ");
      set(errors, errorKey, value);
      continue; // go to next errorKey
    }

    // If errorKey if not a valid dynamic field, add it to others
    others.push(errorDataValue.join(", "));
  }

  return {
    ...errors,
    [othersKey]: others.length ? others.join("\n") : undefined,
  };
};
